import { ReactNode, useCallback, useRef, useState } from "react";
import { createPortal } from "react-dom";

type Props = {
  /** Exact value shown on hover/focus. */
  label: ReactNode;
  children: ReactNode;
  className?: string;
};

/** Clearance between the value and the bubble, leaving room for the arrow. */
const GAP = 14;

/**
 * Small hover/focus readout, portalled to <body>.
 *
 * Portalled rather than absolutely positioned inside the card because these sit in
 * scrolling, rounded, clipped panels — a nested tooltip gets cropped by the first
 * ancestor with overflow set. Keyboard focus gets the same readout as the pointer.
 *
 * Positioning note: the wrapper must NOT carry a keyframe animation. The project's
 * `fade-in` animates `transform: translateY(...)`, which replaces the wrapper's
 * `-translate-x-1/2 / -translate-y-full` outright and drops the bubble straight on
 * top of the value it is describing. Any entrance effect belongs on the inner
 * element, and animates opacity only.
 */
export default function ValueTooltip({ label, children, className }: Props) {
  const ref = useRef<HTMLSpanElement>(null);
  const [pos, setPos] = useState<{
    top: number;
    left: number;
    below: boolean;
  } | null>(null);

  const show = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();

    // Above the value by default. Flips below only when the top of the viewport
    // would clip it, so a figure near the header keeps its readout.
    const below = r.top < 72;

    setPos({
      top: below ? r.bottom + GAP : r.top - GAP,
      left: r.left + r.width / 2,
      below,
    });
  }, []);

  const hide = useCallback(() => setPos(null), []);

  return (
    <>
      <span
        ref={ref}
        tabIndex={0}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        className={`outline-none cursor-help decoration-dotted underline-offset-4 hover:underline focus-visible:underline ${className ?? ""}`}
      >
        {children}
      </span>

      {pos
        ? createPortal(
            <div
              style={{
                top: pos.top,
                left: pos.left,
                // Set as an inline transform so nothing else can replace it.
                transform: `translateX(-50%) ${pos.below ? "" : "translateY(-100%)"}`,
              }}
              className="fixed z-[100001] pointer-events-none"
            >
              <div className="relative px-2.5 py-1.5 rounded-lg bg-surface-elevated border border-edge shadow-overlay text-text-primary text-[13px] font-semibold whitespace-nowrap">
                {label}
                {/* Pointer, so the readout is visibly tied to the figure it describes. */}
                <span
                  aria-hidden
                  className={`absolute left-1/2 w-2 h-2 bg-surface-elevated ${
                    pos.below
                      ? "-top-1 border-l border-t border-edge"
                      : "-bottom-1 border-r border-b border-edge"
                  }`}
                  style={{ transform: "translateX(-50%) rotate(45deg)" }}
                />
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}
